import {addDays, format, parseISO} from "date-fns";

export {
    isValid,
    parse,
    parseISO,
    formatISO,
    addDays,
    subDays,
    subMonths,
    subYears,
    isBefore,
    isAfter,
    format,
    differenceInDays,
    differenceInHours,
    formatDistanceToNow,
    getUnixTime,
    differenceInYears,
    differenceInSeconds,
    min,
    max,
    addSeconds,
} from "date-fns";

// A week's Monday, `weeksAgo` weeks before the given Monday.
export const weekStartFor = (weekStart: DateValue, weeksAgo: number): DateValue =>
    format(addDays(parseISO(weekStart), -7 * weeksAgo), "yyyy-MM-dd");

// "Aug 17th – 23rd", crossing months as "Jul 27th – Aug 2nd".
export const weekLabel = (weekStart: DateValue) => {
    const start = parseISO(weekStart);
    const end = addDays(start, 6);
    return `${format(start, "MMM do")} – ${format(
        end,
        format(start, "MMM") === format(end, "MMM") ? "do" : "MMM do",
    )}`;
};

export const localeDate = (iso: string) => parseISO(iso).toLocaleDateString();

// Built from formatToParts with our own separators: engines disagree on the
// literals .format() emits (U+202F before AM/PM), which breaks hydration.
// The zone is settings.TIME_ZONE.
export const formatBusinessDateTime = (iso: string) => {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/New_York",
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
    }).formatToParts(parseISO(iso));
    const get = (type: Intl.DateTimeFormatPartTypes) =>
        parts.find((part) => part.type === type)?.value ?? "";
    return `${get("month")}/${get("day")}/${get("year")}, ${get("hour")}:${get("minute")}:${get("second")} ${get("dayPeriod")}`;
};

export const deviceTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

// "YYYY-MM-DD" of an instant in an IANA timezone: en-CA formats as ISO.
export const zonedDate = (instant: Date, timeZone: string) =>
    new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).format(instant);

// Hour 0-23 of an instant in an IANA timezone. Safari can emit "24" at midnight.
export const zonedHour = (instant: Date, timeZone: string) =>
    parseInt(
        new Intl.DateTimeFormat("en-US", {
            timeZone,
            hour: "numeric",
            hourCycle: "h23",
        }).format(instant),
    ) % 24;
